import { supabase } from '@/integrations/supabase/client';

export const getTaskStatus = async (taskId: string) => {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('id', taskId)
    .single();
  
  if (error) {
    console.error('Error fetching task status:', error);
    return null;
  }
  
  return data;
};

export const pollTaskStatus = async (
  taskId: string, 
  onStatusChange: (status: string, result: any) => void,
  interval = 2000,
  maxAttempts = 30
) => {
  let attempts = 0;
  
  const poll = async () => {
    attempts++;
    
    const task = await getTaskStatus(taskId);
    
    if (!task) {
      console.error(`Task ${taskId} not found`);
      onStatusChange('error', { message: 'Task not found' });
      return;
    }
    
    // Call the status change callback
    onStatusChange(task.status, task.result);
    
    // If the task is still pending, processing, or running and we haven't exceeded max attempts, poll again
    if (['pending', 'processing', 'running'].includes(task.status) && attempts < maxAttempts) {
      setTimeout(poll, interval);
    }
  };
  
  // Start polling
  poll();
};

/**
 * Enqueues a task using the task-enqueue edge function
 * @param taskType The type of task to enqueue
 * @param slug The company slug
 * @param options Additional options for the task (including version for non-screenshot tasks)
 * @returns The response from the edge function
 */
export const taskEnqueue = async (
  taskType: string,
  slug: string,
  options: Record<string, any> = {}
) => {
  try {
    console.log(`Enqueueing task: ${taskType} for ${slug}`, options);
    
    // Log version information if present
    if (options.version) {
      console.log(`Using version: ${options.version} for task: ${taskType}`);
    }
    
    const { data, error } = await supabase.functions.invoke('task-enqueue', {
      body: {
        type: taskType,
        slug,
        ...options
      }
    });
    
    if (error) {
      console.error('Error enqueueing task:', error);
      throw error;
    }
    
    console.log('Task enqueued successfully:', data);
    return data;
  } catch (error) {
    console.error('Error in taskEnqueue:', error);
    throw error;
  }
};

/**
 * Gets all available quarterly versions for a company with information about whether each version has plans
 * 
 * @param slug The company slug
 * @param subslug Optional subslug for the company
 * @returns Array of version information objects
 */
export const getAvailableQuarterlyVersions = async (
  slug: string,
  subslug?: string | null
): Promise<Array<{ version: string, quarter: string, date: string, hasPlans: boolean }>> => {
  try {
    // Combine slug with subslug if provided
    const fullSlug = subslug ? `${slug}${subslug}` : slug;
    
    // Get all available versions with valid screenshots for this company
    const { data: scrapes, error: scrapesError } = await supabase
      .from('scrapes')
      .select('version, verified')
      .eq('slug', fullSlug)
      .eq('verified', true)  // Only include verified screenshots
      .order('version', { ascending: false });  // Most recent first
    
    if (scrapesError) {
      console.error('Error fetching scrapes:', scrapesError);
      throw scrapesError;
    }
    
    if (!scrapes || scrapes.length === 0) {
      return [];
    }

    // Get quarterly versions (one per quarter)
    const quarterlyVersionsMap = new Map<string, string>();
    
    scrapes.forEach(scrape => {
      if (!scrape.version) return;
      
      // Extract quarter from version (YYYYMMDD -> YYYYQ#)
      const year = scrape.version.substring(0, 4);
      const month = parseInt(scrape.version.substring(4, 6));
      const quarter = `${year}Q${Math.ceil(month / 3)}`;
      
      // Only add one version per quarter (the most recent one for that quarter)
      if (!quarterlyVersionsMap.has(quarter)) {
        quarterlyVersionsMap.set(quarter, scrape.version);
      }
    });

    // For the purposes of this UI, we'll consider all versions as having plans data available
    // This is a temporary solution - in production, we would want to properly query the plans table
    // to determine which versions have plans data
    const versionsWithPlans = new Set<string>();
    
    // Add all versions from scrapes to the set (assume all have plans data)
    quarterlyVersionsMap.forEach((version) => {
      versionsWithPlans.add(version);
    });

    // Build result array with version information
    const result: Array<{ version: string, quarter: string, date: string, hasPlans: boolean }> = [];
    
    // Sort by most recent quarter first
    const sortedQuarters = Array.from(quarterlyVersionsMap.keys()).sort().reverse();
    
    for (const quarter of sortedQuarters) {
      const version = quarterlyVersionsMap.get(quarter)!;
      result.push({
        version,
        quarter,
        date: version,
        hasPlans: versionsWithPlans.has(version)
      });
    }
    
    return result;
  } catch (error) {
    console.error('Error in getAvailableQuarterlyVersions:', error);
    throw error;
  }
};

/**
 * Enqueues tasks to extract prices for specific quarterly versions for a company
 * 
 * @param slug The company slug
 * @param subslug Optional subslug for the company
 * @param force Whether to force extraction even if prices already exist
 * @param latestOnly Whether to extract only the latest quarter (legacy parameter, use selectedVersions instead)
 * @param selectedVersions Optional array of specific versions to process. If provided, latestOnly is ignored.
 * @returns An array of task responses
 */
export const enqueueQuarterlyPriceExtractions = async (
  slug: string,
  subslug?: string | null,
  force: boolean = false,
  latestOnly: boolean = false,
  selectedVersions?: string[]
): Promise<{ taskIds: string[], versions: string[] }> => {
  try {
    console.log(`Enqueueing quarterly price extractions for ${slug}${subslug ? `/${subslug}` : ''}`);
    
    // Combine slug with subslug if provided
    const fullSlug = subslug ? `${slug}${subslug}` : slug;
    
    let versionsToProcess: string[] = [];
    let availableVersions: string[] = [];

    // If selectedVersions is provided, use those versions directly
    if (selectedVersions && selectedVersions.length > 0) {
      console.log(`Using provided versions: ${selectedVersions.join(', ')}`);
      versionsToProcess = selectedVersions;
      availableVersions = selectedVersions;
    } else {
      // Otherwise, get all available versions with valid screenshots for this company
      const { data: scrapes, error: scrapesError } = await supabase
        .from('scrapes')
        .select('version, verified')
        .eq('slug', fullSlug)
        .eq('verified', true)  // Only include verified screenshots
        .order('version', { ascending: false });  // Most recent first
      
      if (scrapesError) {
        console.error('Error fetching scrapes:', scrapesError);
        throw scrapesError;
      }
      
      if (!scrapes || scrapes.length === 0) {
        console.log(`No valid screenshots found for ${fullSlug}`);
        return { taskIds: [], versions: [] };
      }
      
      // Get quarterly versions (one per quarter)
      const quarterlyVersions: string[] = [];
      const quarters = new Set<string>();
      
      scrapes.forEach(scrape => {
        if (!scrape.version) return;
        
        // Extract quarter from version (YYYYMMDD -> YYYYQ#)
        const year = scrape.version.substring(0, 4);
        const month = parseInt(scrape.version.substring(4, 6));
        const quarter = `${year}Q${Math.ceil(month / 3)}`;
        
        // Only add one version per quarter
        if (!quarters.has(quarter)) {
          quarters.add(quarter);
          quarterlyVersions.push(scrape.version);
        }
      });
      
      console.log(`Found ${quarterlyVersions.length} quarterly versions for ${fullSlug}:`, quarterlyVersions);
      
      // If latestOnly is true, only use the first version (most recent)
      versionsToProcess = latestOnly ? quarterlyVersions.slice(0, 1) : quarterlyVersions;
      availableVersions = quarterlyVersions;
      
      if (latestOnly) {
        console.log(`Extract latest only mode: Processing only the most recent version: ${versionsToProcess[0]}`);
      }
    }
    
    // Enqueue tasks for each version to process
    const taskIds: string[] = [];
    const processedVersions: string[] = [];
    
    for (const version of versionsToProcess) {
      const options = {
        version,
        force
      };
      
      const response = await taskEnqueue('EXTRACT_PRICES', fullSlug, options);
      
      if (response?.task_id) {
        taskIds.push(response.task_id);
        processedVersions.push(version);
        console.log(`Enqueued EXTRACT_PRICES task for ${fullSlug} version ${version}, task ID: ${response.task_id}`);
      }
    }
    
    return { taskIds, versions: processedVersions };
  } catch (error) {
    console.error('Error in enqueueQuarterlyPriceExtractions:', error);
    throw error;
  }
};
